import React, {useMemo} from 'react'
import {formatCurrency} from '../../services/utils'
import {calcOfferPriceSummary} from '@whyhotel/ibe-pricing'
import {getLogger} from '../../services/logging'
import {pushDataLayerEvent} from '../../services/dataLayer'
import {booking} from '@whyhotel/common-schema'
import ShoppingCartPaymentInstallment from './ShoppingCartPaymentInstallment'
import {isMonthlyOffer} from '../../services/offerUtils'

const logger = getLogger('ShoppingCartPricingSummary')

export interface ShoppingCartPricingSummaryProps {
  offer: booking.BookingOffer
  additionalServices: booking.ServiceOffer[]
  isCart: boolean
  className?: string
}

const ShoppingCartPricingSummary = ({
  offer,
  additionalServices,
  isCart,
  className = ''
}: ShoppingCartPricingSummaryProps) => {
  const [showItemizedRoomCharges, setShowItemizedRoomCharges] = React.useState(
    false
  )
  const [
    showItemizedIncludedServices,
    setShowItemizedIncludedServices
  ] = React.useState(false)
  const [
    showItemizedAdditionalServices,
    setShowItemizedAdditionalServices
  ] = React.useState(false)
  const [
    showItemizedTaxesAndFees,
    setShowItemizedTaxesAndFees
  ] = React.useState(false)

  const pricingSummary = useMemo(() => {
    return calcOfferPriceSummary(offer, additionalServices)
  }, [offer, additionalServices])

  const {
    itemizedRoomCharges,
    totalRoomCharges,
    itemizedIncludedServices,
    totalIncludedServices,
    itemizedAdditionalServices,
    totalAdditionalServices,
    itemizedTaxesAndFees,
    totalTaxesAndFees,
    total,
    installments
  } = pricingSummary

  logger.debug({
    message: 'Rendering shopping cart pricing summary...',
    data: {
      pricingSummary,
      offer,
      additionalServices
    }
  })

  const handleToggleRoomCharges = () => {
    setShowItemizedRoomCharges(!showItemizedRoomCharges)

    pushDataLayerEvent({
      event: 'Shopping_Cart_Toggle_Room_Charges',
      category: 'IBE',
      value: 1
    })
  }

  const handleToggleIncludedServices = () => {
    setShowItemizedIncludedServices(!showItemizedIncludedServices)
    pushDataLayerEvent({
      event: 'Shopping_Cart_Toggle_Included_Service_Charges',
      category: 'IBE',
      value: 1
    })
  }

  const handleToggleAdditionalServices = () => {
    setShowItemizedAdditionalServices(!showItemizedAdditionalServices)

    pushDataLayerEvent({
      event: 'Shopping_Cart_Toggle_Addon_Service_Charges',
      category: 'IBE',
      value: 1
    })
  }

  return (
    <div className={className}>
      <h2 className="font-semibold text-md mb-2">Pricing:</h2>
      <div className="w-full px-4 mb-2">
        <table className="price-details text-light-primary">
          <tbody>
            {/* room charges */}
            {totalRoomCharges > 0 && (
              <>
                <tr className="font-medium">
                  <td>
                    <span className="mr-2">Total Room Charges</span>
                    <a
                      className="text-xs link text-accent no-underline hover:underline"
                      onClick={() => handleToggleRoomCharges()}
                      onKeyDown={(
                        e: React.KeyboardEvent<HTMLAnchorElement>
                      ) => {
                        if (e.key === 'Enter') {
                          handleToggleRoomCharges()
                        }
                      }}
                      tabIndex={0}
                      aria-label="Toggle itemized room charges."
                    >
                      {showItemizedRoomCharges ? 'hide' : 'show'}
                    </a>
                  </td>
                  <td className="price">{formatCurrency(totalRoomCharges)}</td>
                </tr>
                {showItemizedRoomCharges &&
                  itemizedRoomCharges.map(({name, amount}) => {
                    return (
                      <tr key={name} className="text-xs">
                        <td>{name}</td>
                        <td className="price">{formatCurrency(amount)}</td>
                      </tr>
                    )
                  })}
              </>
            )}

            {/* included services */}
            {totalIncludedServices > 0 && (
              <>
                <tr className="font-medium">
                  <td>
                    <span className="mr-2">Total Included Services</span>
                    <a
                      className="text-xs link text-accent no-underline hover:underline"
                      onClick={() => handleToggleIncludedServices()}
                      onKeyDown={(
                        e: React.KeyboardEvent<HTMLAnchorElement>
                      ) => {
                        if (e.key === 'Enter') {
                          handleToggleIncludedServices()
                        }
                      }}
                      tabIndex={0}
                      aria-label="Toggle itemized included service charges."
                    >
                      {showItemizedIncludedServices ? 'hide' : 'show'}
                    </a>
                  </td>
                  <td className="price">
                    {formatCurrency(totalIncludedServices)}
                  </td>
                </tr>

                {showItemizedIncludedServices &&
                  itemizedIncludedServices.map(({name, amount}, index) => {
                    return (
                      <tr key={index} className="text-xs">
                        <td>{name}</td>
                        <td className="price">{formatCurrency(amount)}</td>
                      </tr>
                    )
                  })}
              </>
            )}

            {/* additional services */}
            {totalAdditionalServices > 0 && (
              <>
                <tr className="font-medium">
                  <td>
                    <span className="mr-2">Total Add-on Services</span>
                    <a
                      className="text-xs link text-accent no-underline hover:underline"
                      onClick={() => handleToggleAdditionalServices()}
                      onKeyDown={(
                        e: React.KeyboardEvent<HTMLAnchorElement>
                      ) => {
                        if (e.key === 'Enter') {
                          handleToggleAdditionalServices()
                        }
                      }}
                      tabIndex={0}
                      aria-label="Toggle itemized add-on service charges."
                    >
                      {showItemizedAdditionalServices ? 'hide' : 'show'}
                    </a>
                  </td>
                  <td className="price">
                    {formatCurrency(totalAdditionalServices)}
                  </td>
                </tr>

                {showItemizedAdditionalServices &&
                  itemizedAdditionalServices.map(({name, amount}, index) => {
                    return (
                      <tr key={`${name}-${index}`} className="text-xs">
                        <td>{name}</td>
                        <td className="price">{formatCurrency(amount)}</td>
                      </tr>
                    )
                  })}
              </>
            )}

            {/* taxes and fees */}
            {totalTaxesAndFees > 0 && (
              <>
                <tr className="font-medium">
                  <td>
                    <span className="mr-2">Total Taxes and Fees</span>
                    <a
                      className="text-xs link text-accent no-underline hover:underline"
                      onClick={() =>
                        setShowItemizedTaxesAndFees(!showItemizedTaxesAndFees)
                      }
                      onKeyDown={(
                        e: React.KeyboardEvent<HTMLAnchorElement>
                      ) => {
                        if (e.key === 'Enter') {
                          setShowItemizedTaxesAndFees(!showItemizedTaxesAndFees)
                        }
                      }}
                      tabIndex={0}
                      aria-label="Toggle itemized taxes and fees."
                    >
                      {showItemizedTaxesAndFees ? 'hide' : 'show'}
                    </a>
                  </td>
                  <td className="price">{formatCurrency(totalTaxesAndFees)}</td>
                </tr>

                {showItemizedTaxesAndFees &&
                  itemizedTaxesAndFees.map(({name, amount}, index) => {
                    return (
                      <tr key={`${name}-${index}`} className="text-xs">
                        <td>{name}</td>
                        <td className="price">{formatCurrency(amount)}</td>
                      </tr>
                    )
                  })}
              </>
            )}

            {/* total amount */}
            <tr className="border-t">
              <td className="font-semibold mr-1">Room Total</td>
              <td className="font-semibold price">{formatCurrency(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {installments?.length > 0 ? (
        <div>
          <h2 className="font-semibold text-md mb-2">
            {installments?.length === 1 ? 'Payment' : 'Payments'}:
          </h2>
          <div className="flex flex-col gap-2">
            {installments.map((installment, index) => {
              const isLast = index === installments.length - 1
              return (
                <ShoppingCartPaymentInstallment
                  key={index}
                  paymentInstallment={installment}
                  isLast={isLast}
                />
              )
            })}
          </div>
        </div>
      ) : (
        ''
      )}

      {!isCart && isMonthlyOffer(offer) && (
        <div
          className="w-full text-xs italic text-center text-gray-600 mt-4"
          role="note"
          aria-label="Monthly rate disclaimer"
        >
          * Monthly rates are estimates. Final taxes and pricing will be
          calculated at checkout. Final amounts may vary slightly from the
          estimate but are typically within a few cents.
        </div>
      )}

      {isCart && isMonthlyOffer(offer) && (
        <div
          className="w-full text-xs italic text-center text-gray-600 mt-4"
          role="note"
          aria-label="Monthly rate disclaimer"
        >
          * Extended stays require guest verification and a soft credit check.
          This does not affect your credit score.
        </div>
      )}
    </div>
  )
}

export default ShoppingCartPricingSummary
